import React from 'react';
import { useAutoRollCountdown } from '@root/hooks';
import { appSelector, type AppSliceState } from '@root/store/slices/appSlice';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import { journeySelector } from '@root/store/slices/journeySlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useImageUrl } from '@shared/components/ImageContext';
import { formatMoney } from '@shared/lib';
import { useTranslation } from 'react-i18next';
import styles from './GameInfoModal.module.scss';

// What each tile on the board means.
const GameInfoModal: React.FC = () => {
  const { t } = useTranslation();
  const { gameInfoModalOpen } = useTypedSelector<AppSliceState>(appSelector);
  const { balance } = useTypedSelector(balanceSelector);
  const { colour } = useTypedSelector(journeySelector);
  const { round } = useTypedSelector(roundSelector);
  const { setGameInfoModalOpen } = useActions();
  const { img, imgProps } = useImageUrl();
  const seconds = useAutoRollCountdown(
    round?.state?.rollDeadline,
    gameInfoModalOpen
  );

  if (!gameInfoModalOpen) return null;

  // Base tile, then whatever is printed on top of it.
  const symbols: {
    base: string;
    overlay?: string;
    title: string;
    meaning: string;
  }[] = [
    {
      base: 'Tile_Ivory',
      title: t('Normal tile'),
      meaning: t('Move here safely. No reward.'),
    },
    {
      base: 'Tile_Ivory',
      overlay: 'MultiplierText_2X',
      title: t('2X multiplier'),
      meaning: t('Pays 2 times your locked roll amount when landed on.'),
    },
    {
      base: 'Tile_Ivory',
      overlay: 'MultiplierText_3X',
      title: t('3X multiplier'),
      meaning: t('Pays 3 times your locked roll amount when landed on.'),
    },
    {
      base: 'Tile_Ivory',
      overlay: 'FreeRoll_Marker',
      title: t('Free roll · +1 ROLL'),
      meaning: t(
        'Your next roll costs {{zero}}. Its rewards still use your locked amount.',
        { zero: formatMoney(balance.currencySymbol, 0) }
      ),
    },
    {
      base: 'Skull',
      title: t('Skull'),
      meaning: t(
        'Landing ends the round. Earlier multiplier rewards remain paid.'
      ),
    },
    {
      base: `Crown_${colour}`,
      title: t('Finish crown · 6X'),
      meaning: t(
        'Reach position 12 exactly. Receive 6 times your locked roll amount.'
      ),
    },
  ];

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t('Game info')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={() => setGameInfoModalOpen(false)}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        {symbols.map((symbol) => (
          <div className={styles.symbolRow} key={symbol.title}>
            <div className={styles.symbol}>
              <img {...imgProps(symbol.base)} alt="" />
              {symbol.overlay && (
                <img
                  className={styles.overlay}
                  {...imgProps(symbol.overlay)}
                  alt=""
                />
              )}
            </div>
            <div className={styles.meaning}>
              <span className={styles.meaningTitle}>{symbol.title}</span>
              <span className={styles.meaningText}>{symbol.meaning}</span>
            </div>
          </div>
        ))}

        <p className={styles.rule}>
          {t(
            'Only the tile landed on triggers. Passing tiles and overshooting do not trigger rewards or skulls.'
          )}
        </p>

        {seconds > 0 && (
          <p className={styles.active}>
            {t('Active round · auto-roll in {{seconds}}s', { seconds })}
          </p>
        )}
      </div>
    </div>
  );
};

export default GameInfoModal;
